import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Droplets, Mountain, TreePine } from "lucide-react";

const PatalpaniWaterfall = () => {
  const navigate = useNavigate();
  const waterfallImage = "/Indore/waterfalls/patalpani0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Patalpani Waterfall",
    "description": "Spectacular waterfall near Indore offering breathtaking views and adventure activities during monsoon",
    "image": waterfallImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Mhow-Mandleshwar Road",
      "addressLocality": "Mhow",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-18:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "8500"
    }
  };

  const highlights = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Spectacular Waterfall",
      description: "300-foot high waterfall creating a mesmerizing cascade during monsoon season"
    },
    {
      icon: <Mountain className="w-6 h-6" />,
      title: "Scenic Location",
      description: "Located amidst lush green hills offering breathtaking views and photo opportunities"
    },
    {
      icon: <TreePine className="w-6 h-6" />,
      title: "Natural Pool",
      description: "Crystal clear natural pool at the base, perfect for a refreshing dip in clean waters"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Adventure Activities",
      description: "Trekking, rock climbing, and photography in beautiful natural surroundings"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free (Parking ₹20-50)" },
    { label: "Best Season", value: "July to October (Monsoon)" },
    { label: "Timings", value: "6:00 AM - 6:00 PM" },
    { label: "Duration", value: "4-6 hours" },
    { label: "Trek Difficulty", value: "Moderate" },
    { label: "Distance from Indore", value: "35 km (1 hour drive)" }
  ];

  const monsoonTips = [
    {
      title: "Safety Precautions",
      points: [
        "Avoid getting too close to waterfall during heavy rains",
        "Wear non-slip shoes for trekking",
        "Check weather conditions before visiting",
        "Travel in groups for safety"
      ]
    },
    {
      title: "Best Time to Visit",
      points: [
        "Early morning (7-10 AM) for fewer crowds",
        "Late afternoon for beautiful lighting",
        "Avoid peak monsoon for safety",
        "Weekdays are less crowded than weekends"
      ]
    }
  ];

  const activities = [
    {
      title: "Waterfall Trekking",
      description: "Trek down to the base of the waterfall through scenic forest paths",
      duration: "2-3 hours",
      difficulty: "Moderate"
    },
    {
      title: "Photography",
      description: "Capture stunning shots of the waterfall and surrounding landscape",
      duration: "1-4 hours",
      difficulty: "Easy"
    },
    {
      title: "Nature Swimming",
      description: "Take a refreshing dip in the natural pool (when safe)",
      duration: "1-2 hours",
      difficulty: "Easy"
    },
    {
      title: "Rock Climbing",
      description: "Climb the rocky terrain around the waterfall area",
      duration: "2-4 hours",
      difficulty: "Advanced"
    }
  ];

  const nearbyAttractions = [
    { name: "Mhow Cantonment", distance: "15 km", time: "20 mins" },
    { name: "Dr. APJ Abdul Kalam Memorial", distance: "18 km", time: "25 mins" },
    { name: "Choral Dam", distance: "25 km", time: "35 mins" },
    { name: "Indore City Center", distance: "35 km", time: "60 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Patalpani Waterfall Near Indore - Monsoon Paradise | Aaoghumen</title>
        <meta name="description" content="Visit Patalpani Waterfall near Indore with Aaoghumen - spectacular 300-foot waterfall perfect for monsoon trips, trekking, and photography. Complete guide with safety tips and activities." />
        <meta name="keywords" content="patalpani waterfall, waterfall near indore, patalpani trek, indore monsoon tourism, aaoghumen patalpani, mhow waterfall, patalpani photography, indore waterfalls, monsoon destinations indore" />
        
        <meta property="og:title" content="Patalpani Waterfall - Spectacular Monsoon Destination | Aaoghumen" />
        <meta property="og:description" content="Experience the breathtaking Patalpani Waterfall near Indore, a 300-foot cascade perfect for adventure, trekking, and monsoon photography." />
        <meta property="og:image" content={waterfallImage} />
        <meta name="twitter:image" content={waterfallImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/patalpani-waterfall" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/patalpani-waterfall" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-blue-600 via-teal-500 to-green-600 overflow-hidden">
          <img
            src={waterfallImage}
            alt="Patalpani Waterfall"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-blue-300">Patalpani Waterfall</span>
              </h1>
              <p className="text-xl mb-6">
                Experience the majestic 300-foot Patalpani Waterfall, one of the most spectacular natural attractions near Indore, perfect for monsoon adventures and nature photography.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.5 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Patalpani Waterfall</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Patalpani Waterfall stands as one of the most magnificent natural attractions near Indore, creating a spectacular sight especially during the monsoon season. This majestic waterfall cascades from a height of approximately 300 feet, creating a thunderous roar that can be heard from a considerable distance. Located on the Mhow-Mandleshwar road, about 35 kilometers from Indore, Patalpani offers visitors an incredible escape into nature's raw beauty and power.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The waterfall gets its name from "Patal" meaning underworld and "Pani" meaning water, suggesting the depth and mystery of the natural pool formed at its base. During the monsoon months from July to October, the waterfall is at its full glory, with crystal clear water rushing down the rocky cliff face surrounded by lush green vegetation. The natural pool at the base provides a refreshing swimming opportunity for adventurous visitors during safer conditions.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, experience the thrill of trekking to this natural wonder, learn about the local ecology, and capture stunning photographs of one of Madhya Pradesh's most beautiful waterfalls. The journey combines adventure, natural beauty, and the refreshing experience of being close to this powerful natural force that showcases the region's monsoon glory.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Waterfall Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Adventures</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-3">{activity.title}</h3>
                  <p className="text-gray-600 mb-4">{activity.description}</p>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Duration: {activity.duration}</span>
                    <span>Difficulty: {activity.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Safety and Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Monsoon Safety & Tips</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {monsoonTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-blue-600 mb-4">{tip.title}</h3>
                  <ul className="space-y-2">
                    {tip.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start text-gray-600">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* What to Bring */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Essential Items to Bring</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Clothing & Gear</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Non-slip trekking shoes</li>
                    <li>• Quick-dry clothing</li>
                    <li>• Waterproof jacket</li>
                    <li>• Extra set of clothes</li>
                    <li>• Towel for swimming</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Photography & Safety</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Waterproof camera cover</li>
                    <li>• First aid kit</li>
                    <li>• Flashlight/headlamp</li>
                    <li>• Mobile phone in waterproof case</li>
                    <li>• Rope for safety</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Food & Hydration</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Sufficient water bottles</li>
                    <li>• Energy snacks and fruits</li>
                    <li>• Packed lunch if staying long</li>
                    <li>• Glucose/energy drinks</li>
                    <li>• Plastic bags for waste</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> Take bus to Mhow, then local transport to waterfall</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Train:</strong> Mhow Railway Station, then taxi/auto to waterfall</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Shared Taxi:</strong> Available from Indore to Mhow</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Indore → Mhow → Mandleshwar Road → Patalpani</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Distance:</strong> 35 km from Indore (1 hour drive)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Available near the trek starting point</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Nature's Power</h2>
              <p className="text-xl mb-6">Visit Patalpani Waterfall with expert guides and safety arrangements</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Mhow-Mandleshwar Road</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-blue-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Waterfall Trip
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-blue-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PatalpaniWaterfall;